import { inject, injectable } from "inversify";
import { Logger, type PointerPosition } from "@designboticteam/core";
import { CursorSnappingService } from "../../../../input/CursorSnappingService";
import { Workspace3DStoreService } from "../../../../services/Workspace3DStoreService";
import { SceneService } from "../../../../stage/SceneService";
import type { ITool } from "../../../../tooling/ITool";
import { Polyline } from "../../../entities/Polyline/Polyline";
import { ShapeEditingService } from "../../../services/ShapeEditingService";
import { ShapeTool } from "../_base/ShapeTool";

const MIN_SEGMENT_LENGTH_M = 0.5;

@injectable()
class PolylineTool extends ShapeTool<Polyline> implements ITool {
	public static readonly toolName: string = "PolylineTool";
	public readonly toolName: string = PolylineTool.toolName;
	protected readonly logger = new Logger("🪢 PolylineTool", {
		color: "#ffffff",
		backgroundColor: "#1d4ed8",
	});

	constructor(
		@inject(ShapeEditingService) protected readonly shapeEditingService: ShapeEditingService,
		@inject(SceneService) protected readonly sceneService: SceneService,
		@inject(CursorSnappingService) protected readonly cursorSnappingService: CursorSnappingService,
		@inject(Workspace3DStoreService) protected readonly workspace3DStoreService: Workspace3DStoreService,
	) {
		super(shapeEditingService);

		this.workspace3DStoreService.store.subscribe(
			(state) => state.tools.nameLabelsDisplayMode,
			() => this.applyLabelsVisibility(),
		);

		this.workspace3DStoreService.store.subscribe(
			(state) => state.tools.dimensionLabelsDisplayMode,
			() => this.applyLabelsVisibility(),
		);

		this.workspace3DStoreService.store.subscribe(
			(state) => state.tools.selectedShape,
			() => this.applyLabelsVisibility(),
		);

		this.logger.log("constructed");
	}

	protected applyLabelsVisibility(): void {
		const { nameLabelsDisplayMode, dimensionLabelsDisplayMode, selectedShape } = this.workspace3DStoreService.store.getState().tools;

		for (const shape of this.shapes) {
			const isShapeSelected = selectedShape === shape;

			shape.setNameLabelsVisibility({
				isVisible: nameLabelsDisplayMode === "all" || (nameLabelsDisplayMode === "selected" && isShapeSelected),
			});

			shape.setDimensionLabelVisible({
				isVisible: dimensionLabelsDisplayMode === "all" || (dimensionLabelsDisplayMode === "selected" && isShapeSelected),
			});
		}
	}

	public dispose(): void {
		this.discardCurrentShape();
		this.logger.log("disposed");
	}

	public onActivate(): void {
		this.logger.log("activated");
	}

	public onDeactivate(): void {
		this.discardCurrentShape();
		this.logger.log("deactivated");
	}

	/**
	 * @description Helpful for tools inheritance
	 */
	protected override createShape() {
		return new Polyline({ label: "Polyline" });
	}

	protected override onShapeAccepted(_shape: Polyline): void {
		this.cursorSnappingService.setSnapExclusion([]);
	}

	protected override discardCurrentShape(): void {
		super.discardCurrentShape();
		this.cursorSnappingService.setSnapExclusion([]);
	}

	public onTap(_position: PointerPosition): void {
		const { worldPosition: point } = this.cursorSnappingService.pickPosition();

		if (point === null) {
			return;
		}

		if (this.currentShape === null) {
			const shape = this.createShape();
			shape.start({ initialPoint: point });

			this.shapes.push(shape);
			this.currentShape = shape;
			this.applyLabelsVisibility();

			this.cursorSnappingService.setSnapExclusion([
				{
					sceneObject: shape.line,
					vertexIndices: [1],
				},
			]);
			this.cursorSnappingService.registerSnapSource(shape.line, () => shape.getPoints());

			this.sceneService.scene.add(shape.sceneObject);
			return;
		}

		const lastConfirmedPoint = this.currentShape.getPoints().at(-1);

		if (lastConfirmedPoint !== undefined && point.distanceTo(lastConfirmedPoint) < MIN_SEGMENT_LENGTH_M) {
			return;
		}

		this.cursorSnappingService.setSnapExclusion([
			{
				sceneObject: this.currentShape.line,
				vertexIndices: [this.currentShape.getPoints().length + 1],
			},
		]);

		this.currentShape.update({ currentPoint: point, confirmPoint: true });

		if (this.currentShape.isComplete()) {
			this.acceptCurrentShape();
		}
	}

	public onPointerMove(_position: PointerPosition): void {
		if (this.currentShape === null) {
			return;
		}

		const { worldPosition: point } = this.cursorSnappingService.pickPosition();

		if (point === null) {
			return;
		}

		this.currentShape.update({ currentPoint: point });
	}

	public onDoubleTap(_position: PointerPosition): void {
		this.acceptCurrentShape();
	}
}

export { PolylineTool };
